import { NgClass } from '@angular/common';
import {
  Component,
  computed,
  ElementRef,
  HostListener,
  inject,
  input,
  model,
  signal,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';

/**
 * A databox the skill can be linked to. Mirrors the shape of the generated
 * `AvailableDataboxView` (SK-114) but kept local so the component stays
 * decoupled from the generated client — the page maps `AvailableDataboxView`
 * -> `DataboxOption` at the load boundary rather than feeding the generated
 * type straight in (same pattern as `ModelOption`).
 *
 * `displayName` and `description` come from the databox contract header and
 * are shown verbatim (not translated). `available` is false for a databox that
 * is registered but never built or switched off: it is shown, marked
 * unavailable, and cannot be newly selected.
 */
export interface DataboxOption {
  databoxId: string;
  displayName: string;
  description: string;
  available: boolean;
}

let nextUniqueId = 0;

/**
 * The Databoxes multi-select in the Advanced Skill Builder (SK-117 / SK-47),
 * shown for the Flex (generic) trait only. A trigger button — cloned from the
 * Model selector — opens a panel of checkable rows (name, description, an
 * "unavailable" badge, a trailing check on the selected rows). Selecting
 * toggles membership and keeps the panel open; clicking outside closes it. The
 * page owns the catalog and the stored selection; this component is
 * presentational.
 *
 * `loadError` renders an inline "unavailable" message in place of the control
 * so the rest of the skill still saves (SK-47 failure scenario). `disabled`
 * renders the control read-only for a user who cannot update the skill.
 */
@Component({
  selector: 'squadbox-assistant-builder-databox-selector',
  imports: [
    TranslateModule,
    fromSeriousSystem.UseArrowIconDirective,
    fromSeriousSystem.UseIconDirective,
    NgClass,
  ],
  template: `
    <!-- The row/column width is applied by the parent page
         (assistant-builder.page.ts) — do not add it here. -->
    <div class="flex flex-col gap-2">
      <label
        [attr.for]="loadError() ? null : buttonId"
        class="typo-caption font-semibold leading-4 h-5 self-start px-1 py-0.5"
        [ngClass]="{
          'text-neutral-400': disabled(),
          'text-neutral-700': !disabled(),
        }"
      >
        {{
          'ASSISTANTS.ASSISTANT_BUILDER.DATABOX_SELECTOR.DROPDOWN_LABEL'
            | translate
        }}
      </label>

      @if (loadError()) {
        <!-- SK-47 failure scenario: the catalog could not be loaded; say so and
             let the rest of the skill save. -->
        <div
          class="typo-p3 text-neutral-500 bg-neutral-100 rounded-lg px-3 py-2 min-h-12 flex items-center border border-neutral-200"
        >
          {{
            'ASSISTANTS.ASSISTANT_BUILDER.DATABOX_SELECTOR.LOAD_ERROR'
              | translate
          }}
        </div>
      } @else {
        <div class="relative">
          <button
            [id]="buttonId"
            type="button"
            class="
              group
              flex flex-row gap-2 items-center w-full
              bg-shades-white rounded-lg px-3 py-2 h-12
              border border-neutral-200 hover:border-primary-300 focus:outline-hidden
              disabled:bg-neutral-100 disabled:border-neutral-200 disabled:hover:border-neutral-200 disabled:pointer-events-none
            "
            [disabled]="disabled()"
            [attr.aria-haspopup]="'listbox'"
            [attr.aria-expanded]="opened()"
            (click)="toggle()"
          >
            <span
              class="typo-p3 group-disabled:text-neutral-400 text-left grow min-w-0 truncate"
              [ngClass]="{ 'text-neutral-400': selectedLabel() === '' }"
            >
              {{
                selectedLabel() ||
                  ('ASSISTANTS.ASSISTANT_BUILDER.DATABOX_SELECTOR.PLACEHOLDER'
                    | translate)
              }}
            </span>
            <svg
              [sdUseArrowIcon]="
                opened() ? 'chevron-small-up' : 'chevron-small-down'
              "
              class="size-6 shrink-0 group-disabled:text-neutral-400"
            ></svg>
          </button>

          @if (opened()) {
            <div
              role="listbox"
              [attr.aria-multiselectable]="true"
              class="
                absolute left-0 right-0 top-[calc(100%+4px)] z-20
                max-h-80 overflow-y-auto
                bg-shades-white rounded-lg border border-neutral-200 shadow-lg p-1
              "
            >
              @for (option of rows(); track option.databoxId) {
                <button
                  type="button"
                  role="option"
                  [attr.aria-selected]="isSelected(option.databoxId)"
                  [disabled]="
                    !option.available && !isSelected(option.databoxId)
                  "
                  class="
                    flex flex-row gap-2 items-start w-full text-left
                    rounded-lg px-3 py-2
                    hover:bg-primary-500/5
                    disabled:opacity-50 disabled:cursor-not-allowed
                  "
                  (click)="toggleOption(option)"
                >
                  <span class="flex flex-col gap-1 flex-1 min-w-0">
                    <span class="typo-p3 font-semibold flex items-center gap-2">
                      <span class="truncate">{{ option.displayName }}</span>
                      @if (!option.available) {
                        <span
                          class="typo-caption text-neutral-500 border border-neutral-200 rounded px-1 py-0.5 shrink-0"
                        >
                          {{
                            'ASSISTANTS.ASSISTANT_BUILDER.DATABOX_SELECTOR.UNAVAILABLE_BADGE'
                              | translate
                          }}
                        </span>
                      }
                    </span>
                    @if (option.description) {
                      <span
                        class="typo-caption text-neutral-500 whitespace-normal"
                        >{{ option.description }}</span
                      >
                    }
                  </span>
                  <svg
                    [sdUseIcon]="checkIcon"
                    class="size-5 text-primary-500 self-center shrink-0"
                    [class.opacity-0]="!isSelected(option.databoxId)"
                  ></svg>
                </button>
              } @empty {
                <div class="typo-p3 text-neutral-500 px-3 py-2">
                  {{
                    'ASSISTANTS.ASSISTANT_BUILDER.DATABOX_SELECTOR.EMPTY'
                      | translate
                  }}
                </div>
              }
            </div>
          }
        </div>

        @if (selectedIds().length > 0) {
          <!-- SK-47: tell the editor that everyone who can use the skill will
               see the selected data (no per-databox permissions in v1). -->
          <p class="typo-caption text-neutral-500 px-1">
            {{
              'ASSISTANTS.ASSISTANT_BUILDER.DATABOX_SELECTOR.SHARED_DATA_NOTICE'
                | translate
            }}
          </p>
        }
      }
    </div>
  `,
})
export class AssistantBuilderDataboxSelectorComponent {
  protected readonly buttonId = `assistant-builder-databox-selector-button-${nextUniqueId++}`;
  protected readonly checkIcon: fromSeriousSystem.IconsType = {
    type: 'uiBasic',
    icon: 'check',
  };

  public readonly disabled = input<boolean>(false);
  public readonly loadError = input<boolean>(false);
  public readonly options = input<DataboxOption[]>([]);
  public readonly selectedIds = model.required<string[]>();

  protected readonly opened = signal(false);

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  /**
   * Close the panel on a click outside the component (SK-117). A click on the
   * trigger or an option is inside the host and does not close it, which is
   * what keeps the panel open across multiple selections.
   */
  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    if (
      this.opened() &&
      !this.host.nativeElement.contains(event.target as Node)
    ) {
      this.close();
    }
  }

  /**
   * Escape closes the panel and returns focus to the trigger (SK-117 review),
   * matching the DS dropdown.
   */
  @HostListener('keydown.escape', ['$event'])
  protected onEscape(event: Event): void {
    if (this.opened()) {
      // Keep the enclosing sd-modal from treating this Escape as Cancel.
      event.stopPropagation();
      this.close();
      this.host.nativeElement
        .querySelector<HTMLButtonElement>(`#${this.buttonId}`)
        ?.focus();
    }
  }

  /**
   * Tabbing out of the component closes the panel (SK-117 review): when focus
   * moves to an element outside the host. A click outside is handled by
   * onDocumentClick.
   */
  @HostListener('focusout', ['$event'])
  protected onFocusOut(event: FocusEvent): void {
    const next = event.relatedTarget as Node | null;
    if (this.opened() && next && !this.host.nativeElement.contains(next)) {
      this.close();
    }
  }

  /**
   * The rows the panel renders: every catalog option, plus a synthetic row for
   * each selected id the catalog does not return (a databox retired, or saved
   * while the catalog was down; SK-114 keeps such ids on purpose). The orphan
   * row carries the Unavailable badge and the check, so `toggleOption` can
   * remove it; without it a stored-but-unknown id could never be deselected
   * (SK-117 review).
   */
  protected readonly rows = computed((): DataboxOption[] => {
    const options = this.options();
    const known = new Set(options.map((option) => option.databoxId));
    const orphans = this.selectedIds()
      .filter((id) => !known.has(id))
      .map((id) => ({
        databoxId: id,
        displayName: id,
        description: '',
        available: false,
      }));
    return [...options, ...orphans];
  });

  /**
   * The text shown in the (closed) trigger: the selected databoxes' display
   * names joined by ", ", or the raw id when a stored id is no longer in the
   * catalog (shown until it is deselected). Empty when nothing is selected, in
   * which case the template shows the placeholder.
   */
  protected readonly selectedLabel = computed((): string => {
    const options = this.options();
    return this.selectedIds()
      .map((id) => options.find((o) => o.databoxId === id)?.displayName ?? id)
      .join(', ');
  });

  protected toggle(): void {
    if (this.disabled()) {
      return;
    }
    this.opened.update((value) => !value);
  }

  protected close(): void {
    this.opened.set(false);
  }

  protected isSelected(id: string): boolean {
    return this.selectedIds().includes(id);
  }

  /**
   * Toggle a databox in or out of the selection. An unavailable databox cannot
   * be newly selected (its row is disabled), but an already-selected one can
   * always be removed. Replaces the array with a new reference so the model
   * signal emits.
   */
  protected toggleOption(option: DataboxOption): void {
    if (this.disabled()) {
      return;
    }
    const selected = this.isSelected(option.databoxId);
    if (!selected && !option.available) {
      return;
    }
    this.selectedIds.update((ids) =>
      selected
        ? ids.filter((id) => id !== option.databoxId)
        : [...ids, option.databoxId],
    );
  }
}
